import React, { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Modal } from 'antd';
import styled from 'styled-components';

import { Colors } from 'context';
import { useProfessions } from 'core/hooks/Growth/useProfessions';
import { CareerCloseButton } from 'molecules/CareerCloseButton/CareerCloseButton';
import { PATHS } from 'router/config';
import { careerType } from 'services/careerType';

export interface IGoalCandidate {
  code: string;
  title: string;
}

/**
 * Выбор карьерной цели с подтверждением (ТЗ 0071, п. 7.3).
 *
 * Проверка в Brave: выбор цели срабатывал с одного клика по карточке каталога,
 * без слова о том, что меняется, а на странице профессии кнопки не было вовсе.
 * Смена цели перестраивает карту, рекомендации курсов и подбор вакансий — это
 * не то действие, которое совершают случайно.
 *
 * Порядок из ТЗ:
 *   1. подтверждение с названием профессии и тем, что изменится;
 *   2. сохранение;
 *   3. переход на `/career` — там видны новая цель и следующее действие.
 *
 * 🔴 CV не трогаем: несовпадение цели и резюме главная предложит поправить
 * ОТДЕЛЬНЫМ действием. Перезаписать заголовок резюме молча значило бы
 * испортить документ, который человек, может быть, уже разослал.
 *
 * Хук, а не компонент с внешним состоянием: вызывающему нужны ровно «спросить»
 * и «куда вставить диалог», а не управление видимостью.
 */
export const useChooseGoalDialog = (): {
  ask: (candidate: IGoalCandidate) => void;
  dialog: ReactNode;
} => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chooseTrack, choosing } = useProfessions();
  const [candidate, setCandidate] = useState<IGoalCandidate | null>(null);

  const confirm = useCallback(async () => {
    if (!candidate) return;
    const ok = await chooseTrack(candidate.code);
    if (!ok) return;
    setCandidate(null);
    navigate(PATHS.careerCenter);
  }, [candidate, chooseTrack, navigate]);

  const dialog = (
    <Modal
      open={candidate !== null}
      // 0073: свой крестик — у antd он подписан «Close» при любой локали.
      closable={false}
      title={
        <TitleRow>
          <span>
            {candidate ? t('careerCenter.goalDialog.title', { title: candidate.title }) : ''}
          </span>
          <CareerCloseButton onClick={() => setCandidate(null)} />
        </TitleRow>
      }
      okText={t('careerCenter.goalDialog.confirm')}
      cancelText={t('careerCenter.goalDialog.cancel')}
      confirmLoading={candidate !== null && choosing === candidate.code}
      onOk={confirm}
      onCancel={() => setCandidate(null)}>
      <Body>{t('careerCenter.goalDialog.body')}</Body>
      <Note>{t('careerCenter.goalDialog.cvNote')}</Note>
    </Modal>
  );

  return { ask: setCandidate, dialog };
};

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Body = styled.p`
  margin: 0 0 8px;
  ${careerType.body()}
`;

const Note = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${careerType.meta()}
`;
